import React, { useEffect, useState } from "react";

import { Header } from "components/Header";
import { SearchBar } from "components/SearchBar";
import { StepRegisterSubClient } from "components/Modals/StepRegister/StepRegisterSubClient";
import { StepRegisterManySubClient } from "components/Modals/StepRegister/StepRegisterManySubClient";
import { DataTable } from "components/DataTable";
import { AlertModal } from "components/Modals/AlertModal";

import { ISubClientsProps, ISuperficialClient } from "interfaces/SubClient";

import {
  handleSubClientActiveSubmit,
  handleSubClientDeleteSubmit,
} from "services/handles/SubClientHandles";

import { DashboardTemplate } from "templates/DashboardTemplate";

import { CheckAlert } from "utils/checkAlert";

import { useSubClientRegister } from "contexts/register/SubClientContext";

import { IFilterWhereOptions, useDataList } from "hooks/useDataList";

import debouncer from "utils/debouncer";
import { useAuth } from "contexts/authContext";
import { TransferSubClientModal } from "components/Modals/TransferSubClientModal";

export function SuperficialClients() {
  const [transfer, setTransfer] = useState(false);
  const [registerModal, setRegisterModal] = useState(false);
  const [registerManyModal, setRegisterManyModal] = useState(false);
  const [activationModal, setActivationModal] = useState(false);
  const [subClient, setSubClient] = useState<ISuperficialClient>();
  const [deleteModal, setDeleteModal] = useState(false);
  const [page, setPage] = useState(1);
  const { role } = useAuth()

  const [filterWhere, setFilterWhere] = useState<IFilterWhereOptions<ISuperficialClient>>()


  const [isAlertModalFetching, setIsAlertModalFetching] = useState<boolean>(false);

  const { setErrors, setSubClient: cleanSubClient } = useSubClientRegister();

  const { data, refetch, isFetching } = useDataList({
    queryKey: 'listAllSuperficialClients',
    route: 'superficial/clients',
    filter: {
      size: 10,
      page,
      params: filterWhere,
      order: {
        name:"ASC"
      },
      relations: {         
        client: true,
        superficialVehicle: true,
        superficialAddress: true
      },
    }
  })

  function handleMenuClick(menuItemId: string, data: ISuperficialClient) {
    setSubClient(data);

    switch (menuItemId) {
      case 'activate':
        setActivationModal(!activationModal);
        break;
      case 'deactivate':
        setDeleteModal(!deleteModal)
        break;
      case 'edit':
        setRegisterModal(true);
        break;
      case 'transfer':
        setTransfer(true);
        break;
    }
  }

  function handleSearchChange(queryString: string) {
    if (queryString) {
      return setFilterWhere([
        // { cpf: search },
        { name: `%${queryString}%` },
        // { email: search },
        // { rg: search },
        { client: { name: `%${queryString}%` } },
        { client: { id: `%${queryString}%` } },
      ])
    }
  }

  const debounceSubclientSearch = debouncer(handleSearchChange, 800);

  useEffect(() => {
    CheckAlert();
  }, []);

  return (
    <DashboardTemplate
      hasTableWrapper
      total={data?.total || 0}
      page={page}
      setPage={setPage}
    >
      <Header title="Sub clientes" />
      <SearchBar
        type="text"
        name="filter"
        id="filter"
        placeholder="Procure por nome, CNPJ, produto..."
        buttonText="Cadastrar sub cliente"
        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
          if (event.target.value) {
            debounceSubclientSearch(event.target.value)
          } else {
            setFilterWhere(undefined);
          }
        }}
        required={false}
        onClick={() => {
          setRegisterModal(true);
        }}
        secondButtonText="Cadastro em lote"
        secondOnClick={() => {
          setRegisterManyModal(true);
        }}
        secondButton
      />

      <DataTable 
        data={data?.items}
        isFetching={isFetching}
        headers={[
          { field: 'name', label: 'Nome do sub-cliente' },
          { field: 'active', label: 'Status do registro', type: 'status' },
          { field: 'client', label: 'Empresa (cliente)', renderColumn: (data) => data.client.name },
        ]}
        menuActions={[
          { id: 'activate', label: 'Ativar cliente', onClick: handleMenuClick, hide: (data) => role === 'master' && !data.active },
          { id: 'deactivate', label: 'Desativar cliente', onClick: handleMenuClick, hide: (data) => role === 'master' && data.active },
          { id: 'edit', label: 'Editar informações', onClick: handleMenuClick },
          { id: 'transfer', label: 'Tranferir subcliente', onClick: handleMenuClick, hide: () => role === 'master'}
        ]}
      />

      <AlertModal 
        isOpen={activationModal}
        title={`Deseja ativar o sub cliente ${subClient?.name}?`}
        text="Um sub-cliente ativado poderá requisitar ocorrências
        à central. Contudo, basta desativá-lo para que isso não ocorra."
        onRequestClose={() => setActivationModal(false)}
        confirmButtonText="Sim, ativar"
        isConfirmFetching={isAlertModalFetching}
        confirmButtonClick={() => {
          setIsAlertModalFetching(true);
          handleSubClientActiveSubmit({
            subClient
          })
        }}
      />
      
      {subClient && <TransferSubClientModal 
        subClient={subClient}
        isOpen={transfer}
        onRequestClose={()=>{ setTransfer(false) }}
      />}

      <AlertModal 
        isOpen={deleteModal}
        title={`Deseja desativar o sub cliente ${subClient?.name}?`}
        text="Um sub-cliente desativado não poderá mais requisitar
        ocorrências à central. Contudo, basta ativá-lo novamente para que 
        o registro ocorra."
        onRequestClose={() => setDeleteModal(false)}
        confirmButtonText="Sim, desativar"
        isConfirmFetching={isAlertModalFetching}
        confirmButtonClick={() => {
          setIsAlertModalFetching(true);
          handleSubClientDeleteSubmit({
            subClient
          })
        }}
      />

      <StepRegisterSubClient
        opened={registerModal}
        setOpened={(value) => {
          setRegisterModal(value);
          cleanSubClient({} as ISubClientsProps);
          setErrors({});
          setSubClient(undefined);
        }}
        subClientData={subClient}
        refetch={refetch}
      />
      
      <StepRegisterManySubClient
        opened={registerManyModal}
        setOpened={(value) => {
          setRegisterManyModal(value);
        }}
      />
    </DashboardTemplate>
  );
}
